<template>
  <!-- A largura é o orçamento do irmão: este painel divide a linha com o
       `CardDutyWithDoctor` dentro de um modal `max-w-4xl`, e os 40% fixos
       deixavam ~139px para o bloco de requisitos do card, quebrando "Formação"
       e "Posição" em duas linhas.
       Dimensionar pelo conteúdo (`w-auto` + `flex-none`) em vez de trocar por
       outra porcentagem: 30% fixos só mudavam a vítima — "Valor: Não calculado"
       passava a quebrar em duas linhas. Assim o painel toma o que precisa, o
       `flex-1` do card fica com o resto, e quando este painel não renderiza
       nada (sem permissão de valor/antecipe) ele deixa de reservar coluna
       vazia. O teto de 40% preserva o limite antigo no modo excepcionalidade,
       onde aparecem dois blocos de valor. -->
  <div class="flex flex-col gap-4 w-full md:w-auto md:flex-none md:max-w-[40%]">
    <template v-if="isExceptionMode">
      <div class="flex flex-col rounded-lg overflow-hidden bg-[#F0F0F4]">
        <div
          class="flex flex-row items-center gap-2 px-4 py-3"
          :class="originalValueContainerClasses"
        >
          <SVGIcon
            :path="
              approvalStatus === 'Approved' || approvalStatus === null
                ? mdiMinusCircleOutline
                : mdiLock
            "
            class="w-4 h-4"
            :class="originalValueIconClasses"
          />
          <span class="font-bold text-xl md:text-2xl" :class="originalValueTextClasses"
            >Valor:</span
          >
          <span class="text-xl md:text-2xl ml-2" :class="originalValueTextClasses">{{
            formattedOriginalValue
          }}</span>
        </div>

        <div
          class="flex flex-row items-center gap-2 px-4 py-3"
          :class="approvedValueContainerClasses"
        >
          <SVGIcon
            v-if="approvalStatus !== 'Rejected'"
            :path="mdiLockOpenVariant"
            class="w-4 h-4"
            :class="approvedValueIconClasses"
          />
          <div v-else class="w-4 h-4 mt-1"></div>

          <span class="font-bold text-xl md:text-2xl" :class="approvedValueTextClasses"
            >Valor:</span
          >
          <span class="text-xl md:text-2xl ml-2" :class="approvedValueTextClasses">{{
            formattedApprovedValue
          }}</span>
        </div>
      </div>
    </template>

    <template v-else>
      <div v-if="canReadPaymentRules" class="flex flex-col gap-2">
        <!-- `whitespace-nowrap`: "Valor:" e a quantia formam uma unidade; a 24px
             o par quebrava em duas linhas com "Não calculado", com o cadeado de
             excepcionalidade ao lado ou com quantia de 6 dígitos. Com o painel
             dimensionado pelo conteúdo, é isso que define a largura pedida. -->
        <div class="flex flex-row items-center px-4 whitespace-nowrap">
          <SVGIcon
            v-if="showExceptionLock"
            :path="mdiLockOpenVariant"
            class="w-4 h-4 text-negative mr-2"
          />
          <strong class="text-xl md:text-2xl">Valor:</strong>
          <span class="text-xl md:text-2xl ml-2" :class="{ 'text-gray-400': isValueNA }">
            {{ formattedOriginalValue }}
          </span>
        </div>

        <!-- Banner de aviso: valor não calculado por falta de política de pagamento -->
        <div
          v-if="isValueNA"
          class="flex items-start gap-2 px-3 py-2 rounded-md text-sm text-left"
          style="
            background: #fef9e7;
            border: 1px solid rgba(241, 194, 27, 0.55);
            border-left: 3px solid #f1c21b;
          "
        >
          <svg
            class="flex-shrink-0 mt-0.5"
            style="color: #b4860e"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path
              d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
            />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
          <div class="flex flex-col gap-1 min-w-0 text-left">
            <p class="m-0 leading-snug text-textPrimary">
              <strong>Valor não calculado.</strong>
              Este plantão pode não estar coberto por uma política de pagamento ativa na escala.
            </p>
            <button
              ref="saibaBtn"
              class="self-start text-xs font-semibold underline underline-offset-2 bg-transparent border-0 p-0 cursor-pointer"
              style="color: #0043ce"
              :aria-expanded="isPolicyPopoverOpen"
              aria-controls="policy-popover"
              @click.stop="togglePolicyPopover"
            >
              Saiba mais
              <svg
                style="display: inline; vertical-align: middle"
                width="10"
                height="10"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.4"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
          </div>
        </div>

        <!-- Popover via Teleport — escapa do overflow-hidden/scroll do modal -->
        <Teleport to="#app">
          <div
            v-if="isPolicyPopoverOpen"
            id="policy-popover"
            ref="popoverEl"
            role="dialog"
            aria-label="Sobre o valor não calculado"
            class="rounded-lg bg-white text-sm text-left"
            :style="popoverStyle"
          >
            <!-- seta -->
            <div
              style="
                position: absolute;
                top: -6px;
                left: 28px;
                width: 12px;
                height: 12px;
                background: #fff;
                border-left: 1px solid #e0e0e8;
                border-top: 1px solid #e0e0e8;
                transform: rotate(45deg);
              "
            />
            <button
              class="absolute top-2 right-2 bg-transparent border-0 cursor-pointer rounded p-1 text-gray-400 hover:text-textPrimary hover:bg-gray-100"
              aria-label="Fechar"
              @click.stop="isPolicyPopoverOpen = false"
            >
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
            <h4 class="m-0 mb-1.5 font-bold text-sm text-textPrimary">
              Por que o valor não é calculado?
            </h4>
            <p class="m-0 mb-2 leading-snug text-gray-600">
              O valor fica como <strong>Não calculado</strong> quando nenhuma política de pagamento
              ativa se aplica a este plantão específico.
            </p>
            <p class="m-0 mb-1.5 leading-snug text-gray-600">Causas mais comuns:</p>
            <ul class="m-0 mb-2 pl-4 leading-snug text-gray-600" style="list-style: disc">
              <li>Nenhuma política criada na escala deste plantão</li>
              <li>
                Regras existentes não cobrem <strong>turno</strong>, <strong>equipe</strong> ou
                <strong>posição</strong> deste plantão
              </li>
              <li>Vigência da política expirada na data do plantão</li>
            </ul>
            <div
              class="rounded text-xs leading-relaxed"
              style="padding: 8px 10px; background: #f4f4f8; color: #52526f"
            >
              Sugestão: valide com o gestor da escala se
              <strong class="text-textPrimary">deveria haver valor</strong> para este plantão e
              revise as políticas de pagamento aplicáveis.
            </div>
          </div>
        </Teleport>
      </div>

      <div v-if="canReadAntecipe" class="flex flex-col gap-2 items-start px-4 py-3">
        <span v-if="requestAntecipe" class="bg-gray-200 rounded py-1 px-3 text-sm"
          >Adiantamento solicitado</span
        >
        <span v-if="antecipeWasPaid" class="bg-gray-200 rounded py-1 px-3 text-sm"
          >Adiantamento pago</span
        >
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted } from 'vue'
import SVGIcon from '@/components/SVGIcon.vue'
import { mdiMinusCircleOutline, mdiLockOpenVariant, mdiLock } from '@mdi/js'
import { usePermissionsStore } from '@/pinia-store/usePermissionsStore'
import { AuthorizationPermissions, AuthorizationResourcesAndSubjects } from '@/types/authorization'

type ApprovalStatus = 'Approved' | 'Rejected' | null

const props = defineProps<{
  originalValue: number | null | undefined
  approvedValue: number | null | undefined
  requestAntecipe: boolean | undefined
  antecipeWasPaid: boolean | undefined
  isExceptionMode: boolean
  approvalStatus?: ApprovalStatus
  scheduleId?: number
  showExceptionLock?: boolean
}>()

const permissionsStore = usePermissionsStore()

const canReadPaymentRules = computed(() => {
  if (!props.scheduleId) return false
  return permissionsStore.check({
    permission: AuthorizationPermissions.StationPaymentRulesReading,
    resource: {
      objectType: AuthorizationResourcesAndSubjects.Schedule,
      objectId: props.scheduleId
    }
  })
})

const canReadAntecipe = computed(() => {
  if (!props.scheduleId) return false
  return permissionsStore.check({
    permission: AuthorizationPermissions.StationAntecipeReading,
    resource: {
      objectType: AuthorizationResourcesAndSubjects.Schedule,
      objectId: props.scheduleId
    }
  })
})

const formatCurrency = (value: number | null | undefined): string | null => {
  if (value === null || value === undefined) return null
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)
}

const formattedOriginalValue = computed(
  () => formatCurrency(props.originalValue) || 'Não calculado'
)
const formattedApprovedValue = computed(
  () => formatCurrency(props.approvedValue) || 'Não calculado'
)

const isValueNA = computed(() => props.originalValue == null && !props.isExceptionMode)

const isPolicyPopoverOpen = ref(false)
const saibaBtn = ref<HTMLElement | null>(null)
const popoverEl = ref<HTMLElement | null>(null)
const popoverPos = ref({ top: 0, left: 0 })

const popoverStyle = computed(() => ({
  position: 'fixed' as const,
  top: `${popoverPos.value.top}px`,
  left: `${popoverPos.value.left}px`,
  width: '320px',
  zIndex: 9999,
  border: '1px solid #E0E0E8',
  boxShadow: '0 12px 32px rgba(12,12,13,0.18), 0 2px 6px rgba(12,12,13,0.08)',
  padding: '14px'
}))

function togglePolicyPopover() {
  if (!isPolicyPopoverOpen.value && saibaBtn.value) {
    const rect = saibaBtn.value.getBoundingClientRect()
    const popoverWidth = 320
    const left = Math.min(rect.left, window.innerWidth - popoverWidth - 16)
    popoverPos.value = { top: rect.bottom + 8, left: Math.max(8, left) }
  }
  isPolicyPopoverOpen.value = !isPolicyPopoverOpen.value
}

function handleGlobalClick(e: MouseEvent) {
  if (!isPolicyPopoverOpen.value) return
  const target = e.target as Node
  if (popoverEl.value?.contains(target) || saibaBtn.value?.contains(target)) return
  isPolicyPopoverOpen.value = false
}

function handleEsc(e: KeyboardEvent) {
  if (e.key === 'Escape') isPolicyPopoverOpen.value = false
}

onMounted(() => {
  document.addEventListener('click', handleGlobalClick)
  document.addEventListener('keydown', handleEsc)
})
onUnmounted(() => {
  document.removeEventListener('click', handleGlobalClick)
  document.removeEventListener('keydown', handleEsc)
})

const originalValueContainerClasses = computed(() => {
  if (!props.isExceptionMode) return ''
  if (props.approvalStatus === 'Approved')
    return 'bg-[#F0F0F4] rounded-lg border-2 border-[#F0F0F4]'
  if (props.approvalStatus === 'Rejected')
    return 'rounded-lg border-2 border-primary30 overflow-hidden bg-[#E2E2E9]'
  return 'bg-[#F0F0F4]'
})

const originalValueTextClasses = computed(() => {
  if (props.isExceptionMode && props.approvalStatus === 'Approved') return 'text-gray-400'
  return 'text-[#212529]'
})

const originalValueIconClasses = computed(() => {
  if (props.isExceptionMode && props.approvalStatus === 'Rejected') return 'text-primary30'
  return 'text-negative'
})

const approvedValueContainerClasses = computed(() => {
  if (!props.isExceptionMode) return ''
  if (props.approvalStatus === 'Approved') return 'bg-[#FCE5E8]'
  if (props.approvalStatus === 'Rejected') return 'bg-[#F0F0F4]'
  return 'bg-[#FCE5E8]'
})

const approvedValueTextClasses = computed(() => {
  if (props.isExceptionMode && props.approvalStatus === 'Rejected') return 'text-gray-400'
  return 'text-[#212529]'
})

const approvedValueIconClasses = computed(() => {
  if (props.isExceptionMode && props.approvalStatus === 'Rejected') return 'text-gray-400'
  return 'text-negative'
})
</script>
